import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { login } from "services/request-api";
import { setRememberedUsername, clearRememberedUsername } from "helpers/rememberedIdentity";

export interface AuthUser {
  token: string;
  username?: string;
  userId?: string | number;
  roles?: string[] | unknown[];
  email?: string;
  name?: string;
  [key: string]: unknown;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  errorMsg: boolean;
  loginUser: (
    user: { username: string; password: string },
    navigate: (path: string) => void,
    options?: { rememberMe?: boolean }
  ) => Promise<void>;
  logoutUser: () => void;
  resetLoginFlag: () => void;
  clearLoginError: () => void;
  hydrate: () => void;
  getAuthUser: () => AuthUser | null;
}

const AUTH_STORAGE_KEY = "authUser";

const authStoreImpl = (set: any, get: any): AuthState => ({
  user: null,
  loading: false,
  error: null,
  errorMsg: false,

  getAuthUser: () => {
    const user = get().user;
    return user;
  },

  hydrate: () => {
    // Cette fonction est appelée pour réinitialiser l'état après le rechargement
    // Le token est déjà dans le store grâce à zustand persist
    const user = get().user;
    if (user?.token) {
      // Le token est automatiquement disponible via useAuthStore.getState()
      console.log("Utilisateur hydraté avec token:", user.token ? "présent" : "absent");
    }
  },

  loginUser: async (credentials, navigate, options) => {
    set({ loading: true, error: null, errorMsg: false });
    try {
      const data = await login({
        username: credentials.username,
        password: credentials.password,
      });
      
      if (!data.success) {
        throw new Error(data.message || "Invalid credentials");
      }
      // data.data = item (token, userId, username, roles) après formatApiResponse
      const payload = data.data ?? data;
      const token = payload?.token || payload?.accessToken || payload?.access_token;
      if (!token) {
        console.error("Token non trouvé dans la réponse:", data);
        throw new Error("Token d'authentification non reçu");
      }
      const userData = {
        ...payload,
        token,
        roles: payload?.roles ?? [],
      };
      
      set({ user: userData, loading: false, error: null, errorMsg: false });
      if (options?.rememberMe) {
        setRememberedUsername(credentials.username);
      }
      navigate("/dashboard-alertes");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      set({ error: msg, loading: false, errorMsg: true });
    }
  },

  logoutUser: () => {
    set({ user: null });
    clearRememberedUsername();
  },

  resetLoginFlag: () => {
    set({ error: null, errorMsg: false });
  },

  clearLoginError: () => {
    set({ error: null, errorMsg: false });
  },
});

export const useAuthStore = create<AuthState>()(
  persist(authStoreImpl, {
    name: AUTH_STORAGE_KEY,
    storage: createJSONStorage(() => sessionStorage),
  })
);
